

// components/BirthdayBanner.jsx
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';

// Neon glow animation using CSS variables for two colors (symmetrical glow)
const neonGlow = keyframes`
  0% {
    box-shadow:
      0 0 5px var(--c1),
      0 0 10px var(--c2),
      0 0 20px var(--c1);
  }
  100% {
    box-shadow:
      0 0 10px var(--c2),
      0 0 20px var(--c1),
      0 0 30px var(--c2);
  }
`;

const calculateFontSize = (msg) => {
  const text = msg ?? '';
  const wordCount = text.trim().split(/\s+/).length;

  if (wordCount <= 10) return 1.8;    
  if (wordCount <= 20) return 1.5;    
  if (wordCount <= 30) return 1.3;    
  if (wordCount <= 40) return 1.2;    
  return 1.1;                        
};

// Banner wrapper styled-component with dynamic colors, background, and optional image
const BannerWrapper = styled.div`
  --c1: ${({ neonColor1 }) => neonColor1};
  --c2: ${({ neonColor2 }) => neonColor2};
  width: 100%;
  height: 80%;
  padding: 1rem;
  margin: 1rem 0;
  background: ${({ background }) => background};
  ${({ backgroundImage }) => backgroundImage && `
    background-image: url(${backgroundImage});
    background-size: cover;
    background-position: center;
  `}
  ${'' /* border-radius: 12px; */}
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  /* apply symmetrical neon glow around entire element */
  animation: ${neonGlow} 1.5s infinite alternate;
  /* ensure glow respects border radius */
  border: 2px solid var(--c1);
  color: ${({ neonText }) => neonText};
`;

// Text inside the banner
const BirthdayText = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-family: ${({ fontFamily }) => fontFamily || 'inherit'};
  font-size: ${({ fontSize }) => fontSize}rem;
`;

const BirthdayBanner = ({
  birthdayMessages,
  neonColor1,
  neonColor2,
  neonText,
  background,
  backgroundImage,
  fontFamily,
  interval = 5000,
}) => {
  const [index, setIndex] = useState(0);
  const hasMultiple = birthdayMessages.length > 1;

  useEffect(() => {
    if (!hasMultiple) return;
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % birthdayMessages.length);
    }, interval);
    return () => clearInterval(id);
  }, [birthdayMessages, hasMultiple, interval]);

  if (!birthdayMessages.length) return null;

  const current = birthdayMessages[index];
  const text = current.title ?? current.messageDetails?.greeting ?? '🎉 Happy Birthday! 🎉';

  return (
    <BannerWrapper
      neonColor1={neonColor1}
      neonColor2={neonColor2}
      neonText={neonText}
      background={background}
      backgroundImage={backgroundImage}
    >
      <BirthdayText fontSize={calculateFontSize(text)} fontFamily={fontFamily}>{text} 🎂🥳🎁</BirthdayText>
    </BannerWrapper>
  );
};

BirthdayBanner.propTypes = {
  birthdayMessages: PropTypes.array.isRequired,
  neonColor1: PropTypes.string,
  neonColor2: PropTypes.string,
  neonText: PropTypes.string,
  background: PropTypes.string,
  backgroundImage: PropTypes.string,
  fontFamily: PropTypes.string,
  interval: PropTypes.number,
};

BirthdayBanner.defaultProps = {
  neonColor1: '#ff007f',
  neonColor2: '#00e0ff',
  neonText: '#ffffff',
  background: '#111',
  backgroundImage: null,
  fontFamily: 'assistant',
  interval: 5000,
};

export default BirthdayBanner;
